import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Mail } from "lucide-react";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

const SECTIONS = [
  {
    id: "eligibility",
    title: "Eligibility",
    body: [
      "You must be at least 18 years of age to access or use our website, purchase products, or use our services.",
      "By using our website or purchasing products, you represent and warrant that:",
      { list: [
        "you are at least 18 years old",
        "you have the legal capacity to enter into binding agreements",
        "your use of our website and products complies with all applicable laws and regulations",
      ]},
      "We reserve the right to refuse service, terminate accounts, or cancel orders at our discretion.",
    ],
  },
  {
    id: "products-services",
    title: "Products and Services",
    body: [
      "EndaG1 offers peptide and wellness-related products through its website and related channels.",
      "All product descriptions, pricing, availability, formulations, packaging, and specifications are subject to change without notice.",
      "We reserve the right to:",
      { list: [
        "limit quantities sold",
        "refuse or cancel orders",
        "discontinue products",
        "correct pricing or typographical errors",
        "restrict sales in certain jurisdictions",
      ]},
      "We make reasonable efforts to ensure product information is accurate; however, we do not warrant that product descriptions, images, or other content are complete, current, or error-free.",
    ],
  },
  {
    id: "no-medical-advice",
    title: "No Medical Advice",
    body: [
      "The information provided by EndaG1, including through our website, product descriptions, customer support, marketing materials, blogs, emails, or social media, is provided for general informational purposes only.",
      "Our products are not intended to diagnose, treat, cure, or prevent any disease unless expressly approved by applicable regulatory authorities.",
      "Nothing on our website or in our communications constitutes medical advice, healthcare advice, diagnosis, or treatment recommendations.",
      "You should consult a qualified healthcare professional before using any product, especially if you:",
      { list: [
        "are pregnant or nursing",
        "have a medical condition",
        "are taking medication",
        "have allergies or sensitivities",
      ]},
      "You assume full responsibility for your use of our products.",
    ],
  },
  {
    id: "research-use",
    title: "Research and Intended Use Disclaimer",
    body: [
      "Certain products sold by EndaG1 may be intended solely for lawful research, laboratory, analytical, or educational purposes where identified as such.",
      "Products identified for research purposes are not intended for human consumption, medical use, veterinary use, injection, or therapeutic application unless expressly stated otherwise in writing by EndaG1.",
      "Customers are solely responsible for ensuring that their purchase, use, and handling of products complies with all applicable laws, regulations, and industry standards.",
      "EndaG1 reserves the right to refuse or cancel any order suspected of misuse or unlawful activity.",
    ],
  },
  {
    id: "orders-payment",
    title: "Orders and Payment",
    body: [
      "By placing an order, you agree that:",
      { list: [
        "all information provided is accurate and complete",
        "you are authorized to use the payment method submitted",
        "you will pay all charges incurred",
      ]},
      "Payments are processed through third-party payment processors. EndaG1 does not guarantee uninterrupted access to payment services and is not responsible for third-party payment platform failures.",
      "Orders are not considered accepted until confirmed by EndaG1.",
      "We reserve the right to:",
      { list: [
        "reject or cancel orders",
        "limit order quantities",
        "restrict purchases",
        "require additional verification",
        "refuse transactions suspected of fraud or unauthorized activity",
      ]},
    ],
  },
  {
    id: "shipping",
    title: "Shipping and Delivery",
    body: [
      "Shipping times are estimates only and are not guaranteed.",
      "EndaG1 is not responsible for delays caused by:",
      { list: [
        "carriers",
        "customs authorities",
        "weather events",
        "supply chain disruptions",
        "events beyond our reasonable control",
      ]},
      "Risk of loss and title transfer to you upon delivery to the shipping carrier unless otherwise required by law.",
      "Customers are responsible for ensuring products may lawfully be imported into their jurisdiction.",
    ],
  },
  {
    id: "returns-refunds",
    title: "Refund Policy",
    body: [
      "All sales are considered final.",
      "Due to the sensitive nature and research use of our products, EndaG1 does not accept returns, refunds, or exchanges once an order has been shipped.",
      "If your order arrives damaged, defective, or incorrect, please contact us within 7 days of delivery and we will review your claim on a case-by-case basis.",
      "We reserve the right to deny any refund or replacement request where:",
      { list: [
        "the request falls outside the 7-day damaged/incorrect notification window",
        "products have been opened, used, altered, or damaged after delivery",
        "products were improperly stored or handled",
        "fraudulent or abusive activity is suspected",
        "the request otherwise violates our stated policies",
      ]},
    ],
  },
  {
    id: "accounts",
    title: "User Accounts",
    body: [
      "If you create an account, you are responsible for:",
      { list: [
        "maintaining the confidentiality of your login credentials",
        "restricting access to your account",
        "all activities occurring under your account",
      ]},
      "You agree to notify us immediately of any unauthorized use or security breach.",
      "We reserve the right to suspend or terminate accounts at our discretion.",
    ],
  },
  {
    id: "prohibited",
    title: "Prohibited Uses",
    body: [
      "You agree not to:",
      { list: [
        "use our products unlawfully",
        "misuse products contrary to instructions or intended use",
        "violate applicable laws or regulations",
        "interfere with website security or functionality",
        "use automated systems to access our website",
        "infringe intellectual property rights",
        "transmit harmful, fraudulent, or misleading content",
      ]},
      "Any unauthorized or unlawful use may result in termination of access and potential legal action.",
    ],
  },
  {
    id: "ip",
    title: "Intellectual Property",
    body: [
      "All website content, including text, graphics, logos, branding, product names, designs, images, videos, and software, is owned by or licensed to EndaG1 and is protected by intellectual property laws.",
      "You may not reproduce, distribute, modify, publish, or exploit our content without prior written consent.",
    ],
  },
  {
    id: "warranties",
    title: "Disclaimer of Warranties",
    body: [
      "To the fullest extent permitted by law, our website, products, and services are provided \"as is\" and \"as available\" without warranties of any kind, whether express, implied, statutory, or otherwise.",
      "We disclaim all warranties, including:",
      { list: [
        "merchantability",
        "fitness for a particular purpose",
        "non-infringement",
        "uninterrupted availability",
        "accuracy or reliability of information",
      ]},
      "We do not warrant that products or services will meet your expectations or produce any specific results.",
    ],
  },
  {
    id: "liability",
    title: "Limitation of Liability",
    body: [
      "To the fullest extent permitted by law, EndaG1 and its directors, officers, employees, affiliates, contractors, suppliers, and service providers shall not be liable for any:",
      { list: [
        "indirect",
        "incidental",
        "consequential",
        "special",
        "punitive",
        "exemplary damages",
      ]},
      "including loss of profits, loss of data, personal injury, business interruption, or product misuse arising from or related to your use of our website, products, or services.",
      "In all cases, EndaG1's total aggregate liability shall not exceed the amount paid by you to EndaG1 for the applicable product or service giving rise to the claim.",
    ],
  },
  {
    id: "indemnification",
    title: "Indemnification",
    body: [
      "You agree to defend, indemnify, and hold harmless EndaG1 and its affiliates, officers, employees, contractors, and representatives from and against any claims, liabilities, damages, losses, costs, and expenses arising from:",
      { list: [
        "your breach of these Terms",
        "your misuse of products",
        "your violation of applicable laws",
        "your infringement of third-party rights",
      ]},
    ],
  },
  {
    id: "privacy",
    title: "Privacy",
    body: [
      "Your use of our website and services is also governed by our Privacy Statement.",
      "By using our website or services, you consent to the collection, use, and disclosure of information as described in our Privacy Statement.",
    ],
  },
  {
    id: "third-party",
    title: "Third-Party Services and Links",
    body: [
      "Our website may contain links to third-party websites, platforms, or services.",
      "We are not responsible for third-party content, products, services, privacy practices, or business practices.",
      "Your interactions with third parties are solely between you and the applicable third party.",
    ],
  },
  {
    id: "governing-law",
    title: "Governing Law",
    body: [
      "These Terms shall be governed by and interpreted in accordance with the laws of the Province of Alberta and the federal laws of Canada applicable therein, without regard to conflict of law principles.",
      "Any disputes arising from these Terms or your use of our website or products shall be subject to the exclusive jurisdiction of the courts located in Alberta, Canada.",
    ],
  },
  {
    id: "severability",
    title: "Severability",
    body: [
      "If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall continue in full force and effect.",
    ],
  },
  {
    id: "changes",
    title: "Changes to These Terms",
    body: [
      "We may update these Terms from time to time.",
      "Updated Terms will be posted on our website with a revised \"Last Updated\" date. Continued use of our website or services after changes are posted constitutes acceptance of the revised Terms.",
    ],
  },
];

function Body({ body }) {
  return body.map((b, i) => {
    if (typeof b === "string") {
      return <p key={i} className="mt-4 text-[15px] leading-[1.75] text-[#C9C9C9]">{b}</p>;
    }
    if (b.list) {
      return (
        <ul key={i} className="mt-3 mb-1 pl-5 space-y-1.5">
          {b.list.map((item, j) => (
            <li key={j} className="text-[14px] leading-[1.7] text-[#C9C9C9] list-disc marker:text-[#4ADE80]">{item}</li>
          ))}
        </ul>
      );
    }
    return null;
  });
}

export default function Terms() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="pt-20 min-h-screen flex flex-col bg-[#0F0F10] text-[#E5E5E5]">
      <SEO
        title="Terms of Service"
        description="EndaG1 Terms of Service — the rules and conditions that govern your use of our research peptide platform."
        canonicalPath="/terms"
      />
      <section className="mx-auto max-w-[900px] w-full px-5 sm:px-8 py-14 sm:py-20 flex-1">
        <Link to="/" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#A8A8A8] hover:text-white transition-colors mb-8" data-testid="terms-back-link">
          <ArrowLeft className="h-4 w-4" /> Back to Home
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
          <p className="text-[11px] uppercase tracking-[0.28em] text-[#4ADE80]">Legal</p>
          <h1 className="mt-4 font-serif-editorial text-4xl sm:text-5xl md:text-6xl text-[#F5F5F5] leading-tight">Terms of Service</h1>
          <p className="mt-4 text-sm text-[#7a7a7a]">Last Updated: May 7, 2026</p>
        </motion.div>

        <div className="mt-10 sm:mt-14 space-y-6">
          <p className="text-[15px] leading-[1.75] text-[#C9C9C9]">
            These Terms of Service (&ldquo;Terms&rdquo;) govern your access to and use of the websites, products, services, content, and related offerings provided by EndaG1 Inc. and its affiliates (collectively, &ldquo;EndaG1&rdquo;, &ldquo;we&rdquo;, &ldquo;our&rdquo;, or &ldquo;us&rdquo;).
          </p>
          <p className="text-[15px] leading-[1.75] text-[#C9C9C9]">
            By accessing our website, purchasing products, creating an account, or otherwise using our services, you agree to be legally bound by these Terms. If you do not agree to these Terms, you must not use our website or services.
          </p>
        </div>

        {/* Table of contents */}
        <nav aria-label="Table of contents" data-testid="terms-toc" className="mt-14 border border-white/10 bg-[#171717] p-6 sm:p-7">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#4ADE80] mb-3">Sections</div>
          <ol className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
            {SECTIONS.map((s, i) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className="text-[#C9C9C9] hover:text-[#4ADE80] transition-colors">
                  <span className="text-[#7a7a7a] font-mono mr-2">{String(i + 1).padStart(2, "0")}.</span>
                  {s.title}
                </a>
              </li>
            ))}
            <li>
              <a href="#contact" className="text-[#C9C9C9] hover:text-[#4ADE80] transition-colors">
                <span className="text-[#7a7a7a] font-mono mr-2">{String(SECTIONS.length + 1).padStart(2, "0")}.</span>
                Contact Information
              </a>
            </li>
          </ol>
        </nav>

        {/* Sections */}
        <div className="mt-16 space-y-16">
          {SECTIONS.map((s, i) => (
            <section key={s.id} id={s.id} data-testid={`terms-section-${s.id}`} className="scroll-mt-24">
              <div className="flex items-baseline gap-3">
                <span className="text-[11px] font-mono text-[#4ADE80]">{String(i + 1).padStart(2, "0")}</span>
                <h2 className="font-display text-2xl sm:text-3xl text-[#F5F5F5] tracking-tight">{s.title}</h2>
              </div>
              <div className="mt-3 pl-9">
                <Body body={s.body} />
              </div>
            </section>
          ))}

          {/* Contact */}
          <section id="contact" data-testid="terms-section-contact" className="scroll-mt-24">
            <div className="flex items-baseline gap-3">
              <span className="text-[11px] font-mono text-[#4ADE80]">{String(SECTIONS.length + 1).padStart(2, "0")}</span>
              <h2 className="font-display text-2xl sm:text-3xl text-[#F5F5F5] tracking-tight">Contact Information</h2>
            </div>
            <div className="mt-3 pl-9">
              <p className="text-[15px] leading-[1.75] text-[#C9C9C9]">
                If you have questions regarding these Terms, please contact:
              </p>
              <div className="mt-6 border border-white/10 bg-[#171717] p-6 max-w-md">
                <div className="font-display text-lg text-[#F5F5F5]">EndaG1 Inc.</div>
                <Link to="/#contact" data-testid="terms-contact-form-link" className="mt-4 inline-flex items-center gap-2 bg-[#4ADE80] text-[#0F0F10] px-5 py-2.5 uppercase tracking-[0.14em] text-[11px] hover:bg-[#6FE998] transition-colors">
                  <Mail className="h-4 w-4" /> Contact Us
                </Link>
                <a href="mailto:info@endag1.com" className="mt-3 block text-sm text-[#7a7a7a] hover:text-[#4ADE80] transition-colors" data-testid="terms-contact-email">info@endag1.com</a>
              </div>
            </div>
          </section>
        </div>
      </section>
      <Footer />
      <div className="h-16 lg:hidden" aria-hidden="true" />
    </div>
  );
}
